import { createFileRoute, Link, useNavigate, notFound } from "@tanstack/react-router";
import { useState } from "react";
import { BadgeCheck, Check, ChevronRight, Clock3, Headphones, LockKeyhole, ShoppingCart, Star } from "lucide-react";
import { getProduct, type Product } from "@/data/catalog";
import { Button } from "@/components/ui/button";
import { Container, meta } from "@/components/store/page-parts";
import { ProductCard } from "@/components/store/product-card";
import { ProductAnimation } from "@/components/store/product-animation";
import { SupportActions } from "@/components/store/support-actions";
import { useStore, normalizeDbProduct } from "@/lib/store-context";
import { formatBdt, formatCurrency } from "@/lib/pricing";
import { toast } from "sonner";

export const Route = createFileRoute("/products/$productSlug")({
  loader: async ({ params }) => {
    // 1. Try local catalog
    const staticProduct = getProduct(params.productSlug);
    if (staticProduct) return staticProduct;

    // 2. Try backend API
    try {
      const res = await fetch(`/api/products/${encodeURIComponent(params.productSlug)}`);
      if (res.ok) {
        const data = await res.json();
        if (data.product) {
          return normalizeDbProduct(data.product);
        }
      }
    } catch {
      // ignore
    }

    throw notFound();
  },
  head: ({ loaderData }) => meta(`${loaderData?.name ?? "Product"} — betteraccess.store`, loaderData?.description ?? "Verified digital product."),
  component: Detail,
  notFoundComponent: () => (
    <Container className="py-24 text-center">
      <h1 className="text-3xl font-black">Product not found</h1>
      <Button asChild className="mt-5">
        <Link to="/products">Browse products</Link>
      </Button>
    </Container>
  ),
});

function Detail() {
  const p = Route.useLoaderData();
  const [variant, setVariant] = useState(p.variants.find(v => !v.soldOut)?.id ?? p.variants[0]?.id ?? "");
  const v = p.variants.find(x => x.id === variant) ?? p.variants[0];
  const { add, buyNow, currency, products } = useStore();
  const navigate = useNavigate();

  const addItem = () => {
    if (!v) return;
    add(p, v.id);
    toast.success("Added to cart");
  };

  return (
    <>
      <Container className="py-5 sm:py-6">
        <div className="flex min-w-0 items-center gap-1 overflow-hidden text-[10px] text-muted-foreground sm:gap-2 sm:text-xs">
          <Link to="/products">Products</Link>
          <ChevronRight className="size-3 shrink-0" />
          <Link to="/products/category/$categorySlug" params={{ categorySlug: p.categorySlug }} className="shrink-0">
            {p.category}
          </Link>
          <ChevronRight className="size-3 shrink-0" />
          <span className="truncate">{p.name}</span>
        </div>
        <div className="mt-5 grid gap-6 sm:mt-7 sm:gap-8 lg:grid-cols-2">
          <div className={`shine-surface relative grid aspect-[16/11] place-items-center lg:aspect-[5/4] rounded-3xl border ${p.accent}`}>
            <ProductAnimation animation={p.animation} label={p.name} fallback={p.icon} large />
          </div>
          <div className="lg:py-5">
            <div className="flex flex-wrap gap-2">
              <span className="rounded-sm bg-primary/10 px-2 py-1 text-[10px] font-extrabold uppercase text-primary">{p.category}</span>
              <span className="rounded-sm bg-success/10 px-2 py-1 text-[10px] font-extrabold uppercase text-success">{p.fulfillment}</span>
            </div>
            <h1 className="mt-4 text-2xl font-black sm:text-4xl">{p.name}</h1>
            <div className="mt-3 flex items-center gap-3 text-xs">
              <span className="flex items-center gap-1 font-bold">
                <Star className="size-4 fill-rating text-rating" />
                {p.rating}
              </span>
              <span className="text-muted-foreground">{p.reviews} verified reviews</span>
            </div>
            <p className="mt-4 text-sm leading-6 text-muted-foreground sm:mt-5 sm:text-base sm:leading-7">{p.description}</p>
            <fieldset className="mt-6 sm:mt-7">
              <legend className="mb-3 text-sm font-extrabold">Choose your plan</legend>
              <div className="grid gap-2 sm:grid-cols-2">
                {p.variants.map(x => (
                  <button
                    key={x.id}
                    disabled={x.soldOut}
                    onClick={() => setVariant(x.id)}
                    className={`relative rounded-xl border p-3 text-left sm:p-4 ${
                      variant === x.id ? "border-primary bg-primary/5 ring-1 ring-primary" : "bg-card"
                    } disabled:opacity-45`}
                  >
                    <span className="block text-sm font-bold">{x.name}</span>
                    <span className="mt-1 block text-lg font-black">{formatCurrency(x.price, currency)}</span>
                    {x.oldPrice && <span className="text-xs text-muted-foreground line-through">{formatCurrency(x.oldPrice, currency)}</span>}
                    {x.soldOut && <span className="absolute right-3 top-3 text-[10px] font-bold uppercase text-muted-foreground">Sold out</span>}
                  </button>
                ))}
              </div>
            </fieldset>
            <div className="mt-5 rounded-2xl border bg-secondary/60 p-3 sm:mt-6 sm:p-4">
              <div className="flex flex-col gap-1 min-[390px]:flex-row min-[390px]:items-center min-[390px]:justify-between">
                <span className="text-sm text-muted-foreground">Your price</span>
                <strong className="text-xl sm:text-2xl">
                  {v ? formatCurrency(v.price, currency) : "—"}{" "}
                  <small className="ml-2 text-xs text-primary">{v ? `≈ ${formatBdt(v.price)} payable` : ""}</small>
                </strong>
              </div>
              <div className="mt-3 flex items-center gap-2 border-t pt-3 text-xs font-semibold text-success">
                <Clock3 className="size-4" /> {p.fulfillment} · Estimated {p.eta}
              </div>
            </div>
            <div className="mt-5 grid gap-2 min-[390px]:grid-cols-2 sm:gap-3">
              <Button size="lg" onClick={addItem}>
                <ShoppingCart /> Add to cart
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={() => {
                  if (v) {
                    buyNow(p, v.id);
                    void navigate({ to: "/checkout" });
                  }
                }}
              >
                Buy now
              </Button>
            </div>
            <div className="mt-6 grid grid-cols-1 gap-2 text-left text-[10px] min-[390px]:grid-cols-3 min-[390px]:text-center font-bold text-muted-foreground">
              <span>
                <LockKeyhole className="mr-2 inline size-4 text-primary min-[390px]:mx-auto min-[390px]:mb-1 min-[390px]:block min-[390px]:size-5" />
                Protected checkout
              </span>
              <span>
                <BadgeCheck className="mr-2 inline size-4 text-primary min-[390px]:mx-auto min-[390px]:mb-1 min-[390px]:block min-[390px]:size-5" />
                Verified access
              </span>
              <span>
                <Headphones className="mr-2 inline size-4 text-primary min-[390px]:mx-auto min-[390px]:mb-1 min-[390px]:block min-[390px]:size-5" />
                Order support
              </span>
            </div>
          </div>
        </div>
        <div className="mt-9 grid gap-6 border-t py-8 sm:mt-12 sm:py-10 lg:grid-cols-[1fr_.7fr]">
          <div>
            <h2 className="text-2xl font-black">What’s included</h2>
            <div className="mt-5 grid gap-2 min-[390px]:grid-cols-2 sm:gap-3">
              {p.includes.map(x => (
                <div key={x} className="flex items-center gap-3 rounded-md bg-secondary p-3 text-sm font-semibold">
                  <Check className="size-4 text-success" />
                  {x}
                </div>
              ))}
            </div>
          </div>
          <aside className="rounded-lg border bg-card p-5">
            <h2 className="font-black">Delivery information</h2>
            <p className="mt-3 text-sm leading-6 text-muted-foreground">
              This is an <strong>{p.fulfillment.toLowerCase()}</strong> product. Access information appears in your order page after the
              shown delivery window.
            </p>
            <div className="mt-4">
              <SupportActions compact />
            </div>
          </aside>
        </div>
        <SectionRelated current={p.slug} products={products} />
      </Container>
    </>
  );
}

function SectionRelated({ current, products }: { current: string; products: Product[] }) {
  return (
    <section className="border-t py-10">
      <h2 className="mb-6 text-2xl font-black">You may also like</h2>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {products
          .filter(p => p.slug !== current)
          .slice(0, 4)
          .map(p => (
            <ProductCard key={p.slug} product={p} />
          ))}
      </div>
    </section>
  );
}
